import { useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { appleProfileUrl, getMailboxInfo } from '@/features/auth/native-mailbox-api'

const MAILBOX_INFO_KEY = ['native-mailbox-info'] as const

const SETUP_GUIDE_URL = 'https://docs.langmail.me/guides/mail-clients#iphone-and-ipad'

// The settings "Set up iPhone or iPad" section. Downloads an Apple
// configuration profile that fills in Mail, Calendar and Contacts — iOS Mail
// consumes none of our autodiscovery records (it resolves setup against Apple's
// own provider database), so without this the user lands on the manual
// IMAP/POP screen.
//
// Only the action lives here; the walkthrough (install the downloaded profile
// under VPN & Device Management, the "Not Signed" warning, what re-running this
// does to the app password) lives in the docs guide linked below.
//
// The action is a real navigation, NOT a fetch: iOS only offers to install a
// profile when the file arrives via a top-level navigation in Safari.
export function AppleProfile() {
  const { data: info, isLoading } = useQuery({ queryKey: MAILBOX_INFO_KEY, queryFn: getMailboxInfo })

  if (isLoading || !info) {
    // No active native mailbox (or still loading) — nothing to set up.
    return null
  }

  return (
    <div className="mb-8">
      <p className="mb-1 font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
        iPhone and iPad
      </p>
      <p className="mb-3 text-[13px] text-muted-foreground italic">
        Set up Mail, Calendar and Contacts in one tap — no server names or passwords to type.
      </p>

      <div className="rounded-xl border border-border bg-secondary p-4">
        <Button
          onClick={() => {
            window.location.assign(appleProfileUrl)
          }}
          type="button"
        >
          Set up iPhone or iPad
        </Button>

        <p className="mt-3 text-[13px] text-muted-foreground">
          Open this page <span className="font-medium text-foreground">in Safari</span>, on the iPhone or iPad you want
          to set up, then tap the button above.{' '}
          <a className="underline" href={SETUP_GUIDE_URL} rel="noopener noreferrer" target="_blank">
            Read the setup guide
          </a>{' '}
          for what iOS does with the downloaded profile.
        </p>
      </div>
    </div>
  )
}
